import { useNavigate, useSearch } from "@tanstack/react-router"
import type { ExpandedState, SortingState, Updater } from "@tanstack/react-table"
import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { Client } from "urql"

import type { Lookup, PageInfo } from "@/graphql/codegen/graphql"
import { SortingOrder } from "@/graphql/codegen/graphql"
import { useLookupWarehouses } from "@/hooks/useLookupWarehouses"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import { useThrottle } from "@/hooks/useThrottle"
import type { Option } from "@/types/form-fields"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

/**
 * Search parameters for warehouse receiving table hooks
 */
export interface WarehouseReceivingTableSearchParams {
  query?: string
  warehouseAreaIds?: string[]
  field?: string
  order?: SortingOrder
}

/**
 * Store selectors interface for warehouse receiving table stores
 */
export interface WarehouseReceivingTableStoreSelectors<TData, TParams> {
  data: TData[]
  fetching: boolean
  executeSearch: (client: Client, params: TParams, skipCache?: boolean) => void
  loadMore: (client: Client, params: TParams) => void
  clearStore: () => void
  pageInfo: PageInfo | undefined
}

/**
 * Configuration for warehouse receiving table hooks
 */
export interface WarehouseReceivingTableConfig<
  TData,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  TSortField extends string,
  TParams extends WarehouseReceivingTableSearchParams,
> {
  store: WarehouseReceivingTableStoreSelectors<TData, TParams>
  searchParamsRoute: string
  navigationRoute: string
  buildSearchParams: (baseParams: WarehouseReceivingTableSearchParams) => TParams
  isExpandable?: boolean
  queryClient: Client
}

/**
 * Return type for the useWarehouseReceivingTableBase hook
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export interface UseWarehouseReceivingTableBaseReturn<TData, TSortField extends string> {
  data: TData[]
  isFetching: boolean
  searchQuery: string
  warehouseAreaIds: string[]
  warehouses: Lookup[]
  warehouseOptions: Option<string>[]
  isWarehousesFetching: boolean
  sortingState: SortingState
  tableContainerRef: RefObject<HTMLDivElement>
  pageInfo: PageInfo | undefined
  handleRefetch: () => void
  handleSearchChange: (query: string) => void
  handleFilterChange: (warehouseIds: string[]) => void
  handleFilterRemove: (warehouseId: string) => void
  handleFilterClear: () => void
  handleSortingChange: (updaterOrValue: Updater<SortingState>) => void
  handleScroll: () => void
  // Optional features
  expandedState?: ExpandedState
  handleExpandedChange?: (updaterOrValue: Updater<ExpandedState>) => void
}

/**
 * Generic base hook for warehouse receiving table functionality.
 * Extracts common logic for search, filter, sort, scroll, and warehouse lookup.
 *
 * @template TData - Type of data items in the table (e.g., GoodsReceipt, GoodsReceiptPallet)
 * @template TSortField - Type of sort field enum (e.g., GoodsReceiptSortField)
 * @template TParams - Type of search parameters (extends WarehouseReceivingTableSearchParams)
 */
export const useWarehouseReceivingTableBase = <
  TData,
  TSortField extends string,
  TParams extends WarehouseReceivingTableSearchParams,
>({
  store,
  searchParamsRoute,
  navigationRoute,
  buildSearchParams,
  isExpandable = false,
  queryClient,
}: WarehouseReceivingTableConfig<TData, TSortField, TParams>): UseWarehouseReceivingTableBaseReturn<
  TData,
  TSortField
> => {
  const [expandedState, setExpandedState] = useState<ExpandedState>({})
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const searchParams = useSearch({
    from: searchParamsRoute as any,
    strict: true,
  })
  const { query = "", warehouseAreaIds = [], field: sortField, order } = searchParams

  const navigate = useNavigate({ from: navigationRoute as any })

  const { warehouses, isFetching: isWarehousesFetching } = useLookupWarehouses()
  const warehouseOptions: Option<string>[] = useMemo(
    () => warehouses.map((w) => ({ value: w.id, label: w.label })),
    [warehouses]
  )

  const sortingState: SortingState = useMemo(() => {
    if (!sortField) {
      return []
    }

    const tableSortField = upperSnakeCaseToCamelCase(sortField)

    return [
      {
        id: tableSortField,
        desc: order === SortingOrder.Desc,
      },
    ]
  }, [sortField, order])

  const { data, fetching, executeSearch, loadMore, clearStore, pageInfo } = store

  useEffect(() => {
    return () => {
      clearStore()
    }
  }, [clearStore])

  const searchParamsForStore = useMemo(
    () => buildSearchParams(searchParams as WarehouseReceivingTableSearchParams),
    [buildSearchParams, searchParams]
  )

  useEffect(() => {
    executeSearch(queryClient, searchParamsForStore)

    if (isExpandable) {
      setExpandedState({})
    }
  }, [executeSearch, searchParamsForStore, queryClient, isExpandable])

  const handleRefetch = useCallback(() => {
    executeSearch(queryClient, searchParamsForStore, true)

    if (isExpandable) {
      setExpandedState({})
    }
  }, [executeSearch, queryClient, searchParamsForStore, isExpandable])

  const [throttledLoadMore] = useThrottle(() => loadMore(queryClient, searchParamsForStore), 200)

  const handleScroll = useScrollLoadMore({
    fetching,
    loadMore: throttledLoadMore,
    scrollContainerRef: tableContainerRef,
  })

  const handleSearchChange = useCallback(
    (searchQuery: string) => {
      ;(navigate as any)({
        search: (prev: Record<string, any>) => ({
          ...prev,
          query: searchQuery || undefined,
        }),
        replace: true,
      })
    },
    [navigate]
  )

  const handleFilterChange = useCallback(
    (warehouseIds: string[]) => {
      ;(navigate as any)({
        search: (prev: Record<string, any>) => ({
          ...prev,
          warehouseAreaIds: warehouseIds.length > 0 ? warehouseIds : undefined,
        }),
        replace: true,
      })
    },
    [navigate]
  )

  const handleFilterRemove = useCallback(
    (warehouseId: string) => {
      const updatedIds = warehouseAreaIds.filter((id: string) => id !== warehouseId)
      ;(navigate as any)({
        search: (prev: Record<string, any>) => ({
          ...prev,
          warehouseAreaIds: updatedIds.length > 0 ? updatedIds : undefined,
        }),
        replace: true,
      })
    },
    [warehouseAreaIds, navigate]
  )

  const handleFilterClear = useCallback(() => {
    ;(navigate as any)({
      search: (prev: Record<string, any>) => ({
        ...prev,
        warehouseAreaIds: undefined,
      }),
      replace: true,
    })
  }, [navigate])

  const handleSortingChange = (updaterOrValue: Updater<SortingState>) => {
    const newSorting = typeof updaterOrValue === "function" ? updaterOrValue(sortingState) : updaterOrValue

    if (newSorting.length > 0) {
      const sort = newSorting[0]
      const key = sort.id
      const field = toUpperSnakeCase(key) as TSortField
      const params = {
        query,
        warehouseAreaIds,
        field,
        order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc,
      }

      ;(navigate as any)({
        search: params,
        replace: true,
      })
    }
  }

  const handleExpandedChange = isExpandable
    ? (updaterOrValue: Updater<ExpandedState>) => {
        setExpandedState(updaterOrValue)
      }
    : undefined

  return {
    data,
    isFetching: fetching,
    searchQuery: query,
    warehouseAreaIds,
    warehouses,
    warehouseOptions,
    isWarehousesFetching,
    sortingState,
    tableContainerRef,
    pageInfo,
    handleRefetch,
    handleSearchChange,
    handleFilterChange,
    handleFilterRemove,
    handleFilterClear,
    handleSortingChange,
    handleScroll,
    ...(isExpandable && { expandedState, handleExpandedChange }),
  }
}
